// The application's sections and who sees them in the navigation (Gate 2B, Design Gate 1.2).
// Visibility is convenience only: every screen and request is still authorized by the server,
// and a section opened from an address still shows only what the server returns.
import type { MeResponse } from '@sivan/shared';
import type { IconName } from './ui/Icon';

export type SectionId =
  | 'home'
  | 'handover'
  | 'schedule'
  | 'master-data'
  | 'team'
  | 'apartment'
  | 'more'
  | 'structure'
  | 'defects'
  | 'portal'
  | 'labor'
  | 'expenses'
  | 'inventory'
  | 'admin';

export interface Section {
  readonly id: SectionId;
  /** The section's name (the screen title, and the accessible name of its links). */
  readonly label: string;
  /** A shorter text for the phone's tab bar (the accessible name stays `label`). */
  readonly short?: string;
  readonly icon: IconName;
}

export const SECTIONS: Readonly<Record<SectionId, Section>> = {
  home: { id: 'home', label: 'הבית שלי', short: 'הבית', icon: 'home' },
  handover: { id: 'handover', label: 'מסירות', icon: 'clipboard' },
  schedule: { id: 'schedule', label: 'יומן מסירות', short: 'יומן', icon: 'calendar' },
  'master-data': { id: 'master-data', label: 'ניהול נתוני אב', icon: 'building' },
  team: { id: 'team', label: 'צוות והרשאות', icon: 'users' },
  // One apartment (U2): opened from the handover lists and Master Data, never listed itself.
  apartment: { id: 'apartment', label: 'דירה', icon: 'building' },
  more: { id: 'more', label: 'עוד', icon: 'more' },
  // The building · floor · apartment navigator (U3).
  structure: { id: 'structure', label: 'מבנה ודירות', short: 'מבנה', icon: 'layers' },
  // ניהול ליקויים and the supplier portal (DR-196).
  defects: { id: 'defects', label: 'ניהול ליקויים', short: 'ליקויים', icon: 'alert' },
  portal: { id: 'portal', label: 'הליקויים שלי', short: 'ליקויים', icon: 'clipboard' },
  // Modules not built yet (docs/03): reachable by address, listed under "עוד" as coming later.
  labor: { id: 'labor', label: 'שעות רג׳י', icon: 'clock' },
  expenses: { id: 'expenses', label: 'קופה קטנה', icon: 'file' },
  inventory: { id: 'inventory', label: 'מחסן החברה', icon: 'building' },
  admin: { id: 'admin', label: 'ניהול מערכת', icon: 'settings' },
};

/** Sections without a module behind them yet. */
export const PLACEHOLDERS: readonly SectionId[] = ['labor', 'expenses', 'inventory', 'admin'];

/** The section in a hash ("#handover/…" → handover); Home for anything unknown. */
export function sectionOf(hash: string): SectionId {
  const id = hash.replace(/^#/, '').split('/')[0] ?? '';
  return Object.hasOwn(SECTIONS, id) ? (id as SectionId) : 'home';
}

/** Every business permission key the user holds, in any scope. */
export function allKeys(me: MeResponse): ReadonlySet<string> {
  return new Set([
    ...me.access.organization,
    ...me.access.allProjects,
    ...me.access.projects.flatMap((p) => p.permissions),
  ]);
}

/** Whether the user may see a section's screen at all (anywhere they work). */
export function canSee(me: MeResponse | null, id: SectionId): boolean {
  if (id === 'home' || id === 'more') return true;
  if (me === null) return false;
  const keys = allKeys(me);
  switch (id) {
    case 'handover':
      return keys.has('handover.handover.view');
    case 'structure':
      return keys.has('projects.unit.view') && keys.has('projects.structure.view');
    case 'schedule':
      return keys.has('schedule.calendar.view');
    case 'master-data':
      return (
        keys.has('projects.unit.view') ||
        keys.has('organization.project.create') ||
        keys.has('projects.project.edit')
      );
    case 'team':
      return keys.has('projects.team.view') || keys.has('organization.employee.view');
    case 'defects':
      return keys.has('defects.defect.view');
    case 'portal':
      return keys.has('defects.portal.use');
    default:
      return true;
  }
}

/** A supplier's person: the portal is its whole application (DR-196). */
export function isSupplier(me: MeResponse | null): boolean {
  return me !== null && allKeys(me).has('defects.portal.use');
}

/** Whether the user can change something in a management section (not only look at it). */
export function manages(me: MeResponse | null, id: SectionId): boolean {
  if (me === null) return false;
  const keys = allKeys(me);
  const any = (list: readonly string[]) => list.some((k) => keys.has(k));
  if (id === 'master-data')
    return any([
      'projects.unit.create',
      'projects.unit.edit',
      'projects.allocation.edit',
      'projects.catalog.edit',
      'projects.project.edit',
      'projects.structure.edit',
      'organization.project.create',
    ]);
  if (id === 'team') return any(['organization.access.manage']);
  if (id === 'defects') return any(['defects.defect.manage']);
  return false;
}

/**
 * Management screens this user is offered in the navigation: those they manage, and Master Data
 * for office users who only look at it (it is their work). Field users who cannot change anything
 * there are not offered management screens at all (docs/08 §7: limited users see only their
 * permitted actions); the apartments stay reachable through "מבנה ודירות". The same rule holds
 * for the team screen. Addresses still open, and the server decides every request.
 */
function managementFor(me: MeResponse | null): Section[] {
  if (me === null) return [];
  const keys = allKeys(me);
  const fieldWorker = keys.has('handover.handover.edit') || keys.has('handover.handover.create');
  const offered: Section[] = [];
  if (canSee(me, 'master-data') && (manages(me, 'master-data') || !fieldWorker))
    offered.push(SECTIONS['master-data']);
  if (
    canSee(me, 'team') &&
    (manages(me, 'team') ||
      !fieldWorker ||
      keys.has('organization.employee.view') ||
      // A scheduling coordinator staffs handovers from the team, even when also in the field.
      keys.has('schedule.assignment.assign'))
  )
    offered.push(SECTIONS.team);
  if (canSee(me, 'defects')) offered.push(SECTIONS.defects);
  return offered;
}

/**
 * The navigation for this user. The field work comes first everywhere (Home, structure,
 * handovers, the calendar). A management screen sits in the top bar for someone who manages
 * there; an office user who may only look at Master Data finds it under "עוד". The phone's tab
 * bar always keeps the management screens under "עוד".
 */
export function navigationFor(me: MeResponse | null): {
  readonly desktop: readonly Section[];
  readonly phone: readonly Section[];
  readonly more: readonly Section[];
} {
  // A supplier's person (DR-196) works only in its own portal.
  if (isSupplier(me))
    return { desktop: [SECTIONS.portal], phone: [SECTIONS.portal, SECTIONS.more], more: [] };
  const visible = (ids: readonly SectionId[]) =>
    ids.filter((id) => canSee(me, id)).map((id) => SECTIONS[id]);
  const field = visible(['home', 'structure', 'handover', 'schedule']);
  const management = managementFor(me);
  const managed = management.filter((s) => manages(me, s.id));
  const viewOnly = management.filter((s) => !manages(me, s.id));
  return {
    desktop: [...field, ...managed, ...(viewOnly.length > 0 ? [SECTIONS.more] : [])],
    phone: [...field, SECTIONS.more],
    more: management,
  };
}

/** A group of the rail (desktop and tablet): field work first, then management. */
export interface NavGroup {
  readonly id: 'work' | 'manage';
  readonly label: string;
  readonly sections: readonly Section[];
}

/**
 * The rail on a desktop or tablet: every section the user may open, field work first and the
 * management screens (whether managed or only viewed) in their own group. Modules not built yet
 * are not listed (docs/product-ux 11 · D3); their addresses still open an honest "not available".
 */
const field = new Set<SectionId>(['home', 'structure', 'handover', 'schedule', 'portal']);

export function railFor(me: MeResponse | null): readonly NavGroup[] {
  const { desktop, more } = navigationFor(me);
  const work = desktop.filter((s) => field.has(s.id));
  const groups: NavGroup[] = [{ id: 'work', label: 'עבודה', sections: work }];
  if (more.length > 0) groups.push({ id: 'manage', label: 'ניהול', sections: more });
  return groups;
}
